import DeleteConfirmationDialog from '@components/DeleteConfirmation';
import ModalWrapper from '@components/modal/ModalWrapper';
import { useDeleteChatHistory } from '@hooks/deleteQueries';
import { useFetchChatHistory } from '@hooks/getQueries';
import { useUpdateChatTitle } from '@hooks/postQueries';
import useConfirmationDialog from '@hooks/useDeleteConfirmation';
import { Icon } from '@iconify/react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import ChatHistoryLoadingState from './ChatHistoryLoading';

const drawerWidth = 280;

// Utility to format ISO date string to 'Today', 'Yesterday', or 'MMM d'
function formatChatDate(isoString) {
  const date = new Date(isoString);
  const now = new Date();

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfToday.getDate() - 1);

  if (date >= startOfToday) {
    return 'Today';
  } else if (date >= startOfYesterday) {
    return 'Yesterday';
  } else {
    return date.toLocaleString('default', { month: 'short', day: 'numeric' });
  }
}

function ChatSidebar({
  id,
  activeChatId,
  setActiveChatId,
  handleNewChatClick,
  setIsTranscriptListUpdated,
  isFullScreen,
  setCurrentChatId,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: chatHistoryData, isPending: isPendingChatHistory } = useFetchChatHistory(id);
  const [openEditTitleModal, setOpenEditTitleModal] = useState(false);
  const [editingChatId, setEditingChatId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuChatId, setMenuChatId] = useState(null);
  const { mutateAsync, isPending } = useUpdateChatTitle();
  const { mutateAsync: deleteChatItem, isPending: isDeletingChatItem } = useDeleteChatHistory();
  const { open, show, handleConfirm, handleClose } = useConfirmationDialog();

  const {
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      new_title: '',
    },
  });

  const filteredChats = chatHistoryData?.filter((chat) => {
    const title = chat.chat_title?.toLowerCase() || '';
    const subtitle = chat.subtitle?.toLowerCase() || '';
    return title.includes(searchQuery) || subtitle.includes(searchQuery);
  });

  const handleMenuOpen = (event, chatId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuChatId(chatId);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setEditingChatId(null);
  };

  const handleCloseTitleEditModal = () => {
    setOpenEditTitleModal(false);
    handleMenuClose();
  };
  const handleEditTitle = async () => {
    let { new_title } = getValues();
    let payload = {
      chat_id: menuChatId,
      new_title,
    };
    try {
      await mutateAsync(payload);
      setOpenEditTitleModal(false);
      setMenuAnchorEl(null);
      setMenuChatId(null);
    } catch (error) {
      console.error('Failed to update chat title. Please try again.', error?.detail || error);
    }
  };

  const handleDeleteItem = useCallback(
    (payload) => {
      show(async () => {
        const isCurrentlyActiveChat = payload.chatId === activeChatId;
        await deleteChatItem(payload);
        handleMenuClose();

        // Invalidate chat history after successful deletion
        queryClient.invalidateQueries({ queryKey: ['chat-history', id] });
        // If we deleted the currently active chat, navigate to new conversation
        if (isCurrentlyActiveChat) {
          setActiveChatId('');
          navigate(`/interview/${id}/chat-with-data/new-conversation`);
        }
      });
    },
    [deleteChatItem, show, activeChatId, navigate, id, setActiveChatId],
  );

  const handleChatClick = useCallback(
    (chatId) => {
      setIsTranscriptListUpdated(false);
      setEditingChatId(null);
      setActiveChatId(chatId);
      setCurrentChatId(chatId);
      // Invalidate chat history to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['chat-history', id] });

      navigate(`/interview/${id}/chat-with-data/${chatId}`);
    },
    [
      setIsTranscriptListUpdated,
      setEditingChatId,
      setActiveChatId,
      setCurrentChatId,
      queryClient,
      id,
      navigate,
    ],
  );

  return (
    <Drawer
      variant="permanent"
      PaperProps={{
        sx: {
          position: 'absolute',
          height: `${isFullScreen ? '100%' : 'calc(100vh - 140px)'} `,
          // top: 60,
          left: 0,
          bottom: 0,
          width: drawerWidth,
          boxSizing: 'border-box',
          bgcolor: 'white',
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: '#f0f0f0 transparent',

          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
          },

          '&:hover::-webkit-scrollbar-thumb': {
            backgroundColor: '#ccc',
          },
          '&:hover': {
            scrollbarColor: '#ccc transparent',
          },
        },
      }}
    >
      <Box sx={{ overflow: 'auto' }}>
        <Box position={'sticky'} top={'5px'} zIndex={'99999'} bgcolor={'#ffff'} p={1.5} mt={2}>
          <Button
            variant="contained"
            fullWidth
            size="medium"
            onClick={handleNewChatClick}
            sx={{ mb: 1, display: 'flex', justifyContent: 'center', gap: 0.3, mt: 1 }}
          >
            <Icon icon="tabler:plus" width="20" height="20" /> New chat
          </Button>
          <TextField
            margin="dense"
            fullWidth
            placeholder="Search history..."
            size="small"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
          />
        </Box>
        <List dense>
          {isPendingChatHistory ? (
            <ChatHistoryLoadingState />
          ) : (
            !isPendingChatHistory &&
            filteredChats?.length <= 0 && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={8}
                px={2}
                textAlign="center"
              >
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '50%',
                    mb: 2,
                  }}
                >
                  <Icon icon="line-md:chat-off" width="24" height="24" />
                </Box>

                <Typography variant="h6" component="h3" gutterBottom>
                  No chat history yet
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 240 }}>
                  Start a new conversation to begin exploring and analyzing your data
                </Typography>
              </Box>
            )
          )}

          {!isPendingChatHistory &&
            filteredChats?.map((chat, idx) => (
              <ListItem
                key={idx}
                disablePadding
                sx={{
                  py: 0,
                }}
              >
                <ListItemButton
                  sx={{
                    bgcolor:
                      activeChatId === chat._id || editingChatId === chat._id ? '#ececec' : '',
                    '&:hover .moreBtn': {
                      visibility: 'visible',
                    },
                    position: 'relative',
                  }}
                  dense
                >
                  <ListItemText
                    onClick={() => handleChatClick(chat._id)}
                    primary={
                      <>
                        <Typography variant="caption" color="text.secondary">
                          {formatChatDate(chat.created_date)}
                        </Typography>
                        <Typography variant="body1" color="text.primary" fontWeight={'500'}>
                          {chat.chat_title}
                        </Typography>
                      </>
                    }
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.primary">
                          {chat.subtitle}
                        </Typography>
                      </>
                    }
                  />
                  <IconButton
                    className="moreBtn"
                    size="small"
                    onClick={(e) => {
                      handleMenuOpen(e, chat._id);
                      setEditingChatId(chat._id);
                      setValue('new_title', chat?.chat_title);
                    }}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%) rotate(90deg)',

                      visibility: 'hidden',
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Box>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => setOpenEditTitleModal(true)}>Edit Title</MenuItem>
        <MenuItem
          onClick={() =>
            handleDeleteItem({
              chatId: editingChatId,
            })
          }
        >
          Delete
        </MenuItem>
      </Menu>
      <ModalWrapper
        open={openEditTitleModal}
        onClose={handleCloseTitleEditModal}
        onConfirm={handleEditTitle}
        isPending={isPending}
        title={'Rename Title'}
        isShowTooltip={false}
      >
        <form>
          <Box p={2}>
            <TextField
              {...register('new_title', { required: 'Title is required' })}
              placeholder={'Enter your Title'}
              variant="outlined"
              size="small"
              error={!!errors.new_title}
              helperText={errors.new_title?.message}
              sx={{
                mt: 1,
              }}
              fullWidth
            />
          </Box>
        </form>
      </ModalWrapper>
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title="Delete Chat"
        isPending={isDeletingChatItem}
        content={`This action can’t be undone. Are you sure you want to delete this chat?`}
        okText={isDeletingChatItem ? 'Deleting' : 'Delete'}
      />
    </Drawer>
  );
}

export default ChatSidebar;
